import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Typography,
  Stack,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/vi";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { MRT_Localization_VI } from "material-react-table/locales/vi";
import { fetchUserLoginHistories } from "../../services/userLoginHistory.service";
import AppSnackbar from "components/common/snackbar/AppSnackbar";
import { AppFilterBar } from "components/common/filters";
import { AppButton } from "components/common/buttons";
import { AppSelect, AppDatePicker } from "components/common/inputs";
import DataTable from "components/common/tables/DataTable";

const formatDateParam = (date, boundary) => {
  if (!date) return "";

  const parsedDate = dayjs(date);
  if (!parsedDate.isValid()) return "";

  return parsedDate[boundary]("day").format("YYYY-MM-DD");
};

const UserLoginHistoryTab = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [sorting, setSorting] = useState([
    {
      id: "loginTime",
      desc: true,
    },
  ]);
  const [globalFilter, setGlobalFilter] = useState("");

  // Date filters
  const [fromDate, setFromDate] = useState(() => dayjs().subtract(7, "day"));
  const [toDate, setToDate] = useState(() => dayjs());
  const [selectedPeriod, setSelectedPeriod] = useState("7days");

  // Period presets
  const periodOptions = [
    { value: "3days", label: "3 ngày", days: 3 },
    { value: "7days", label: "7 ngày", days: 7 },
    { value: "1month", label: "1 tháng", days: 30 },
    { value: "3months", label: "3 tháng", days: 90 },
    { value: "custom", label: "Tùy chọn", days: null },
  ];

  // Columns definition
  const columns = useMemo(
    () => [
      {
        accessorKey: "userId",
        header: "User ID",
        size: 60,
      },
      {
        accessorKey: "userName",
        header: "Tên đăng nhập",
        size: 120,
      },
      {
        accessorKey: "fullName",
        header: "Họ tên",
        size: 150,
      },
      {
        accessorKey: "loginTime",
        header: "Thời gian đăng nhập",
        size: 150,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return value ? dayjs(value).format("DD/MM/YYYY HH:mm:ss") : "-";
        },
      },
      {
        accessorKey: "logoutTime",
        header: "Thời gian đăng xuất",
        size: 150,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return value ? (
            dayjs(value).format("DD/MM/YYYY HH:mm:ss")
          ) : (
            <Chip label="Đang online" size="small" color="success" />
          );
        },
      },
      {
        accessorKey: "ipAddress",
        header: "Địa chỉ IP",
        size: 100,
      },
      {
        accessorKey: "deviceInfo",
        header: "Thiết bị",
        size: 200,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return value ? (
            <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
              {value.length > 50 ? `${value.substring(0, 50)}...` : value}
            </Typography>
          ) : (
            "-"
          );
        },
      },
      {
        accessorKey: "browser",
        header: "Trình duyệt",
        size: 80,
      },
      {
        accessorKey: "browserVersion",
        header: "Phiên bản",
        size: 60,
        Cell: ({ cell }) => (
          <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
            {cell.getValue()}
          </Typography>
        ),
      },
    ],
    []
  );

  // Handle period change
  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);

    if (period !== "custom") {
      const option = periodOptions.find((p) => p.value === period);
      if (option && option.days) {
        const newFromDate = dayjs().subtract(option.days, "day");
        setFromDate(newFromDate);
        setToDate(dayjs());
      }
    }
  };

  // Handle custom date changes
  const handleFromDateChange = (newDate) => {
    setFromDate(newDate);
    setSelectedPeriod("custom");
  };

  const handleToDateChange = (newDate) => {
    setToDate(newDate);
    setSelectedPeriod("custom");
  };

  // Fetch data
  const fetchData = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        search: globalFilter || "",
        fromDate: formatDateParam(fromDate, "startOf"),
        toDate: formatDateParam(toDate, "endOf"),
      };

      // Add sorting
      if (sorting.length > 0) {
        params.sortBy = sorting[0].id;
        params.sortOrder = sorting[0].desc ? "desc" : "asc";
      }

      const response = await fetchUserLoginHistories(params);

      if (response.success) {
        setData(response.data.items || []);
        setTotalCount(response.data.totalCount || 0);
      } else {
        setSnackbar({ open: true, message: response.message || "Không thể tải dữ liệu" });
        setData([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error fetching user login history:", error);
      setSnackbar({ open: true, message: "Lỗi khi tải dữ liệu lịch sử đăng nhập" });
      setData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [pagination, sorting, globalFilter, fromDate, toDate]);

  // Effect to fetch data when filters change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Material React Table configuration
  const table = useMaterialReactTable({
    columns,
    data,
    enableGlobalFilter: true,
    enableColumnFilters: false,
    enableColumnActions: false,
    enableColumnOrdering: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    rowCount: totalCount,
    state: {
      isLoading: loading,
      pagination,
      sorting,
      globalFilter,
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    muiTableContainerProps: {
      sx: {
        maxHeight: "calc(100vh - 400px)",
        minHeight: "400px",
      },
    },
    muiTableHeadCellProps: {
      sx: {
        fontWeight: 600,
        backgroundColor: "#f5f5f5",
      },
    },
    muiTableBodyRowProps: {
      sx: {
        "&:nth-of-type(odd)": {
          backgroundColor: "#fafafa",
        },
      },
    },
    muiSearchTextFieldProps: {
      placeholder: "Tìm kiếm theo tên đăng nhập, họ tên...",
      sx: { minWidth: "300px" },
      variant: "outlined",
      size: "small",
    },
    muiTopToolbarProps: {
      sx: {
        backgroundColor: "transparent",
      },
    },
    muiPaginationProps: {
      color: "primary",
      shape: "rounded",
      variant: "outlined",
    },
    localization: MRT_Localization_VI,
    initialState: {
      density: "compact",
      showGlobalFilter: true,
    },
  });

  return (
    <Box>
      {/* Filter Controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <AppFilterBar icon={false}>
          <AppSelect
            value={selectedPeriod}
            onChange={handlePeriodChange}
            label="Khoảng thời gian"
            options={periodOptions}
            sx={{ flex: 1 }}
          />
          <AppDatePicker
            label="Từ ngày"
            value={fromDate}
            onChange={handleFromDateChange}
            sx={{ flex: 1 }}
          />
          <AppDatePicker
            label="Đến ngày"
            value={toDate}
            onChange={handleToDateChange}
            sx={{ flex: 1 }}
          />
          <AppButton
            variant="outlined"
            onClick={fetchData}
            size="small"
            disabled={loading}
          >
            Tải lại
          </AppButton>
        </AppFilterBar>
      </Paper>

      {/* Data Table */}
      <Paper sx={{ overflow: "hidden" }}>
        <DataTable
          columns={columns}
          data={data}
          state={{
            isLoading: loading,
            pagination,
            sorting,
            globalFilter,
          }}
          onPaginationChange={setPagination}
          onSortingChange={setSorting}
          onGlobalFilterChange={setGlobalFilter}
          manualPagination
          manualSorting
          manualFiltering
          rowCount={totalCount}
        />
      </Paper>
      <AppSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity="error"
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default UserLoginHistoryTab;
